import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Plus,
  Search,
  Edit,
  RefreshCw,
  AlertCircle,
  Gauge,
  User,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal, Select } from '../../components/ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface ManagedUser {
  id: string;
  name: string;
  email: string;
  phone?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  createdAt: string;
  limits?: UserLimit[];
}

interface UserLimit {
  operationType: string;
  dailyLimit: number;
  monthlyLimit: number;
  perTransactionLimit: number;
  dailyConsumption: number;
  monthlyConsumption: number;
}

const OPERATION_TYPES = [
  { value: 'PIX_SEND', label: 'Pix - Envio' },
  { value: 'PIX_RECEIVE', label: 'Pix - Recebimento' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'TRANSFER', label: 'Transferência' },
  { value: 'WITHDRAW', label: 'Saque' },
];

export function ManagerUsers() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [limitData, setLimitData] = useState({
    operationType: 'PIX_SEND',
    dailyLimit: 0,
    monthlyLimit: 0,
    perTransactionLimit: 0,
  });

  // Buscar usuários do tenant
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['manager-users'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.managerUsers.list);
      return response.data as { users: ManagedUser[] };
    },
  });

  // Criar usuário
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await api.post(ENDPOINTS.managerUsers.create, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manager-users'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  // Atualizar usuário
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<typeof formData> }) => {
      const response = await api.put(ENDPOINTS.managerUsers.update(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manager-users'] });
      setIsModalOpen(false);
      setEditingUser(null);
      resetForm();
    },
  });

  // Definir limite
  const setLimitMutation = useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: typeof limitData }) => {
      const response = await api.post(ENDPOINTS.limits.setUserLimit(userId), {
        ...data,
        dailyLimit: data.dailyLimit * 100,
        monthlyLimit: data.monthlyLimit * 100,
        perTransactionLimit: data.perTransactionLimit * 100,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manager-users'] });
      setIsLimitModalOpen(false);
      setSelectedUserId(null);
      resetLimitForm();
    },
  });

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '' });
  };

  const resetLimitForm = () => {
    setLimitData({
      operationType: 'PIX_SEND',
      dailyLimit: 0,
      monthlyLimit: 0,
      perTransactionLimit: 0,
    });
  };

  const handleEdit = (user: ManagedUser) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
    });
    setIsModalOpen(true);
  };

  const handleSetLimit = (userId: string) => {
    setSelectedUserId(userId);
    resetLimitForm();
    setIsLimitModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingUser) {
      updateMutation.mutate({ id: editingUser.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleLimitSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUserId) {
      setLimitMutation.mutate({ userId: selectedUserId, data: limitData });
    }
  };

  const users = data?.users || [];
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(cents / 100);
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar usuários</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Gestão de Usuários</h1>
          <p className="text-surface-400">Gerencie os usuários do seu tenant e defina limites</p>
        </div>
        <Button variant="primary" onClick={() => { resetForm(); setEditingUser(null); setIsModalOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Usuário
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome ou email..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary-400" />
            Usuários do Tenant
            <Badge variant="info">{filteredUsers.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-20" />
              ))}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum usuário encontrado</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {filteredUsers.map(user => (
                <div
                  key={user.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                      <User className="w-6 h-6 text-primary-400" />
                    </div>
                    <div>
                      <p className="font-medium text-surface-100">{user.name}</p>
                      <p className="text-sm text-surface-500">{user.email}</p>
                      {user.phone && <p className="text-sm text-surface-500">{user.phone}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {user.limits && user.limits.length > 0 && (
                      <div className="text-right text-sm">
                        <p className="text-surface-400">Limite diário (Pix)</p>
                        <p className="font-medium text-surface-100">
                          {formatCurrency(user.limits.find(l => l.operationType === 'PIX_SEND')?.dailyLimit || 0)}
                        </p>
                      </div>
                    )}
                    <Badge variant={user.status === 'ACTIVE' ? 'success' : user.status === 'SUSPENDED' ? 'warning' : 'error'}>
                      {user.status}
                    </Badge>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleSetLimit(user.id)} title="Definir Limites">
                        <Gauge className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(user)} title="Editar">
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit User Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingUser(null); resetForm(); }}
        title={editingUser ? 'Editar Usuário' : 'Novo Usuário'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            disabled={!!editingUser}
          />
          <Input
            label="Telefone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={createMutation.isPending || updateMutation.isPending}>
              {editingUser ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Set Limit Modal */}
      <Modal
        isOpen={isLimitModalOpen}
        onClose={() => { setIsLimitModalOpen(false); setSelectedUserId(null); resetLimitForm(); }}
        title="Definir Limite de Operação"
      >
        <form onSubmit={handleLimitSubmit} className="space-y-4">
          <Select
            label="Tipo de Operação"
            value={limitData.operationType}
            onChange={(e) => setLimitData({ ...limitData, operationType: e.target.value })}
            options={OPERATION_TYPES}
          />
          <Input
            label="Limite Diário (R$)"
            type="number"
            step="0.01"
            min="0"
            value={limitData.dailyLimit}
            onChange={(e) => setLimitData({ ...limitData, dailyLimit: parseFloat(e.target.value) || 0 })}
          />
          <Input
            label="Limite Mensal (R$)"
            type="number"
            step="0.01"
            min="0"
            value={limitData.monthlyLimit}
            onChange={(e) => setLimitData({ ...limitData, monthlyLimit: parseFloat(e.target.value) || 0 })}
          />
          <Input
            label="Limite por Transação (R$)"
            type="number"
            step="0.01"
            min="0"
            value={limitData.perTransactionLimit}
            onChange={(e) => setLimitData({ ...limitData, perTransactionLimit: parseFloat(e.target.value) || 0 })}
          />
          <div className="p-3 bg-surface-800 rounded-lg text-sm text-surface-400">
            <p className="font-medium text-surface-300 mb-1">Importante:</p>
            <p>Os limites do usuário não podem exceder os limites do tenant.</p>
            <p>Operações acima do limite requerem aprovação.</p>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsLimitModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={setLimitMutation.isPending}>
              Salvar Limite
            </Button>
          </div>
        </form>
      </Modal>
    </motion.div>
  );
}
